import { useEffect, useMemo, useRef, useState } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import SheetModal from '@/components/General/SheetModal';
import Input from '@/components/General/inputs/Input';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Toggle from '@/components/General/inputs/Toggle';
import Chip from '@/components/General/Chip';
import SolidButton from '@/components/General/SolidButton';
import PeopleAdvancedFilter from '@/components/Filters/PeopleAdvancedFilter';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import useFilterStore, { useFilter } from '@/data/store/useFilterStore';

/**
 * AudienceEditorSheet — create/edit a Follow Up audience (spec §6.1
 * Audiences). An audience selects WHO is monitored (optional saved-People
 * filter), WHICH events qualify for attendance, and carries its own
 * attention thresholds (blank = server default).
 *
 * filterJSON/filterLogic use the QuickFilters shape and are sent through
 * follow_up_upsert_audience on save — picking a saved filter copies its
 * definition, "Everyone" clears it (explicit null), and "New filter…"
 * opens the shared People filter builder (its applied result is adopted
 * from the builder's filter-store slot).
 */
const BUILDER_FILTER_ID = 'follow-up-audience';

const digitsOnly = (text) => `${text ?? ''}`.replace(/[^0-9]/g, '');
const toPositiveIntOrNull = (text) => {
    const n = parseInt(text, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
};
const hasFilter = (filterJSON) => !!filterJSON
    && (typeof filterJSON !== 'object' || Object.keys(filterJSON).length > 0);

const previewCount = (preview) => {
    if (preview == null) return null;
    if (typeof preview === 'number') return preview;
    if (typeof preview === 'object') {
        return preview.count ?? preview.peopleCount ?? preview.memberCount ?? preview.total ?? null;
    }
    return null;
};

const AudienceEditorSheet = ({ isOpen, onClose, organizationID, audience = null }) => {
    const isEdit = !!audience?.id;

    const [name, setName] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [attemptGap, setAttemptGap] = useState('');
    const [connectedGap, setConnectedGap] = useState('');
    const [attendanceGap, setAttendanceGap] = useState('');
    // filterDraft: {name?, filterJSON, logic} — null means "Everyone".
    const [filterDraft, setFilterDraft] = useState(null);
    const [selectedSaved, setSelectedSaved] = useState(null); // {id, name}
    const filterBuilderRef = useRef(null);
    const builderFilter = useFilter(BUILDER_FILTER_ID);
    const clearBuilderFilters = useFilterStore((s) => s.clearFilters);

    const { data: allEvents = [] } = Query.events.all({ options: { enabled: isOpen } });
    const { data: quickFilters = [] } = Query.quickFilters.all({ options: { enabled: isOpen } });

    useEffect(() => {
        if (isOpen) {
            setName(audience?.name || '');
            setIsActive(audience?.isActive !== false);
            // Preserve every saved ID even before event labels load.
            setSelectedEvents((audience?.attendanceEventIDs || []).map(id => ({ id })));
            setAttemptGap(audience?.contactAttemptGapDays != null ? `${audience.contactAttemptGapDays}` : '');
            setConnectedGap(audience?.connectedGapDays != null ? `${audience.connectedGapDays}` : '');
            setAttendanceGap(audience?.attendanceGapWeeks != null ? `${audience.attendanceGapWeeks}` : '');
            clearBuilderFilters(BUILDER_FILTER_ID);
            setFilterDraft(hasFilter(audience?.filterJSON)
                ? { name: null, filterJSON: audience.filterJSON, logic: audience.filterLogic || 'AND' }
                : null);
            setSelectedSaved(null);
        }
    }, [isOpen, audience?.id]);

    // Adopt whatever the shared People filter builder last applied into this
    // sheet's dedicated store slot (custom definition or a saved quick
    // filter picked inside the builder — which carries its name).
    useEffect(() => {
        if (!isOpen || !hasFilter(builderFilter?.peopleFilters)) return;
        setFilterDraft({
            name: builderFilter.quickFilter?.name || null,
            filterJSON: builderFilter.peopleFilters,
            logic: builderFilter.filterLogic || 'AND',
        });
        setSelectedSaved(builderFilter.quickFilter?.id
            ? { id: builderFilter.quickFilter.id, name: builderFilter.quickFilter.name }
            : null);
    }, [builderFilter, isOpen]);

    const saveMutation = Mutation.followUp.upsertAudience({
        onSuccess: (data) => {
            const count = previewCount(data?.membershipPreview);
            Toast.show({
                type: 'success',
                text2: count != null
                    ? `Audience saved — covers ${count} ${count === 1 ? 'person' : 'people'}`
                    : 'Audience saved',
            });
            onClose?.();
        },
        onError: (error) => {
            if (!error?.expected) {
                Toast.show({ type: 'error', text2: 'Failed to save the audience. Please try again.' });
            }
        },
    });

    const submitting = saveMutation.isPending;
    const canSubmit = !!name.trim() && !submitting;

    // Resolve an unnamed (custom or just-seeded) draft against the saved
    // People filters so the label reads honestly.
    const matchedSaved = useMemo(() => {
        if (!filterDraft?.filterJSON) return null;
        const key = JSON.stringify(filterDraft.filterJSON);
        return quickFilters.find((f) => JSON.stringify(f.filterJSON) === key
            && (f.logic || 'AND') === (filterDraft.logic || 'AND')) || null;
    }, [quickFilters, filterDraft]);
    const filterLabel = filterDraft
        ? (filterDraft.name || selectedSaved?.name || matchedSaved?.name || 'custom')
        : null;
    const selectorValue = selectedSaved
        || (matchedSaved ? { id: matchedSaved.id, name: matchedSaved.name } : null);

    const handleFilterSelect = (selected) => {
        if (selected?.id) {
            const filter = quickFilters.find((f) => f.id === selected.id);
            if (filter) {
                setFilterDraft({ name: filter.name, filterJSON: filter.filterJSON, logic: filter.logic || 'AND' });
                setSelectedSaved({ id: filter.id, name: filter.name });
                clearBuilderFilters(BUILDER_FILTER_ID);
            }
        } else {
            // Deselected — "Everyone" (clears the filter on save).
            setFilterDraft(null);
            setSelectedSaved(null);
            clearBuilderFilters(BUILDER_FILTER_ID);
        }
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        const payload = {
            name: name.trim(),
            isActive,
            attendanceEventIDs: (selectedEvents || []).map((e) => e.id).filter(Boolean),
            filterJSON: filterDraft?.filterJSON ?? null,
            filterLogic: filterDraft?.logic ?? null,
            contactAttemptGapDays: toPositiveIntOrNull(attemptGap),
            connectedGapDays: toPositiveIntOrNull(connectedGap),
            attendanceGapWeeks: toPositiveIntOrNull(attendanceGap),
        };
        if (isEdit) payload.id = audience.id;
        saveMutation.mutate({ organizationID, payload });
    };

    return (
        <SheetModal
            isOpen={isOpen}
            title={isEdit ? 'Edit Audience' : 'New Audience'}
            cancelText="Cancel"
            confirmText={isEdit ? 'Save Audience' : 'Create Audience'}
            onCancel={onClose}
            onClose={onClose}
            onConfirm={canSubmit ? handleSubmit : null}
            loading={submitting}
        >
            <View style={styles.body}>
                <Input
                    title="Name"
                    placeholder="e.g. Members, Regular Attenders"
                    value={name}
                    maxLength={80}
                    required
                    onUpdate={setName}
                    testID="audience-name-input"
                />

                <View style={styles.toggleRow}>
                    <Text style={styles.toggleLabel}>Active</Text>
                    <Toggle
                        testID="audience-active-toggle"
                        value={isActive}
                        onValueChange={setIsActive}
                    />
                </View>
                {!isActive && (
                    <Text style={styles.hint}>
                        Inactive audiences stop contributing people to monitoring. Anyone explicitly included stays monitored.
                    </Text>
                )}

                <Text style={styles.sectionLabel}>People filter</Text>
                <SheetSelector
                    type="quickFilters"
                    titleOnly
                    isNotOnSurface
                    placeholder="Everyone, or pick a saved people filter"
                    value={selectorValue}
                    onChange={handleFilterSelect}
                    testID="audience-filter-picker"
                />
                <View style={styles.filterRow}>
                    <SolidButton
                        testID="audience-new-filter"
                        size="sm"
                        variant="outlined"
                        type="paths"
                        iconName="funnel-outline"
                        text="New filter…"
                        onPress={() => filterBuilderRef.current?.open()}
                    />
                    {!!filterLabel && (
                        <Chip icon="funnel-outline" colorVariant="textSecondary" testID="audience-custom-filter-chip">
                            {`Filter: ${filterLabel}`}
                        </Chip>
                    )}
                </View>
                <Text style={styles.hint}>
                    Only people matching this filter are monitored for this audience. Leave empty to monitor everyone.
                </Text>
                {/* The shared People filter builder, driven by ref.open() —
                    hidden because it renders its own SheetModal surface. */}
                <View style={styles.builderAnchor} pointerEvents="none">
                    <PeopleAdvancedFilter ref={filterBuilderRef} filterId={BUILDER_FILTER_ID} />
                </View>

                <SheetSelector
                    type="events"
                    title="Qualifying events"
                    multiple
                    placeholder="Select events that count as attendance"
                    value={selectedEvents.map(event => allEvents.find(item => item.id === event.id) || event)}
                    onChange={setSelectedEvents}
                    testID="audience-events-picker"
                />
                <Text style={styles.hint}>
                    Attendance at these events counts toward this audience&rsquo;s attendance check. Attendance at unrelated events doesn&rsquo;t clear the gap.
                </Text>

                <Text style={styles.sectionLabel}>Attention thresholds</Text>
                <Input
                    title="Attempt gap (days)"
                    helpText="Flag when no one has attempted contact within this window. Leave blank for the default."
                    value={attemptGap}
                    maxLength={3}
                    onUpdate={(v) => setAttemptGap(digitsOnly(v))}
                    testID="audience-attempt-gap"
                />
                <Input
                    title="Connected gap (days)"
                    helpText="Flag when no real conversation has connected within this window. Leave blank for the default."
                    value={connectedGap}
                    maxLength={3}
                    onUpdate={(v) => setConnectedGap(digitsOnly(v))}
                    testID="audience-connected-gap"
                />
                <Input
                    title="Attendance gap (weeks)"
                    helpText="Flag when no qualifying attendance is recorded within this window. Leave blank for the default."
                    value={attendanceGap}
                    maxLength={3}
                    onUpdate={(v) => setAttendanceGap(digitsOnly(v))}
                    testID="audience-attendance-gap"
                />
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors }) => ({
    body: { paddingBottom: 16, gap: 4 },
    toggleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    toggleLabel: {
        fontSize: 14,
        color: colors.text,
    },
    sectionLabel: {
        fontSize: 13,
        color: colors.text,
        marginTop: 8,
        marginBottom: 2,
    },
    hint: {
        fontSize: 12,
        color: colors.textSecondary,
        fontStyle: 'italic',
        marginTop: 2,
        marginBottom: 4,
    },
    filterRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 4,
    },
    builderAnchor: {
        position: 'absolute',
        left: -9999,
        top: -9999,
        opacity: 0,
    },
}));

export default AudienceEditorSheet;
